import React, {
	createContext,
	useContext,
} from 'react';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { CargoDetails, CargoReletConsumptionSet } from '@api/features/cargos/getCargoDetails';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import type { CargoPortWithEstimatedValues } from '@api/features/cargos/transformCargos';
import {
	CargoChanges,
	CargoPortChanges,
	OnCreateNewCargoMutator,
	OnCreateNewCargoReletMutator,
	OnDeleteCargoMutator,
	OnDeleteCargoReletMutator,
	OnImportCargoMutator,
	OnUpdateCargo,
	UpdateCargoPort,
	useCargoHandlers,
} from './hooks/useCargoHandlers';
import { ChartererOption, useMiscHandlers } from './hooks/useMiscHandlers';
import { useCentralStore } from './CentralStoreContext';

export type CargoContextType = {
	estimate: GetEstimateDetailsResponse | null;
	estimateLoading: boolean;
	cargos: Array<CargoDetails>;
	cargoPorts: CargoPortWithEstimatedValues[];
	onUpdateCargo: OnUpdateCargo;
	onUpdateCargoPort: (attributes: UpdateCargoPort) => Promise<void> | void;
	copyCargoPortSettings: (cargoPortId: number) => void;
	setSyncCargos: React.Dispatch<React.SetStateAction<boolean>>;
	onCreateNewCargoMutator: OnCreateNewCargoMutator;
	onCreateNewCargoReletMutator: OnCreateNewCargoReletMutator;
	onImportCargoMutator: OnImportCargoMutator;
	onDeleteCargoMutator: OnDeleteCargoMutator;
	onDeleteCargoReletMutator: OnDeleteCargoReletMutator;
	chartererOptions: ChartererOption[];
	charterers: GetCounterpartiesResponse | undefined;
	portOptions: {
		label: string;
		value: number;
	}[];
	refreshEverything: () => Promise<void>;
	pendingCargoPortChanges: CargoPortChanges[];
	pendingCargoChanges: CargoChanges[];
	setPendingCargoPortChanges: (changes: CargoPortChanges[]) => void;
	setPendingCargoChanges: (changes: CargoChanges[]) => void;
	hasPendingChanges: boolean;
	cargoReletConsumptionSet: CargoReletConsumptionSet | null;
	showReletView: boolean;
	handleSetReletView: (value: boolean | ((prev: boolean) => boolean)) => void;
	reletViewByEstimateId: Record<number, boolean>;
	setReletViewByEstimateId: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
	hasCargoRelet: boolean;
};

export const CargoContext = createContext<CargoContextType>(undefined!);
export const useCargoProvider = () => useContext(CargoContext);

const CargoProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const {
		estimate,
		cargos: centralCargos,
		syncCargos,
		setSyncCargos,
		refreshEverything,
		selectedEstimateId,
		setLoading,
	} = useCentralStore();

	const {
		cargos,
		cargoPorts,
		onCreateNewCargoMutator,
		onCreateNewCargoReletMutator,
		onImportCargoMutator,
		onDeleteCargoMutator,
		onDeleteCargoReletMutator,
		onUpdateCargo,
		onUpdateCargoPort,
		copyCargoPortSettings,
		hasPendingChanges,
		pendingCargoChanges,
		pendingCargoPortChanges,
		setPendingCargoPortChanges,
		setPendingCargoChanges,
		cargoReletConsumptionSet,
		showReletView,
		handleSetReletView,
		reletViewByEstimateId,
		setReletViewByEstimateId,
		hasCargoRelet,
	} = useCargoHandlers({
		syncCargos,
		setSyncCargos,
		estimate,
		centralCargos,
		refreshEverything,
		selectedEstimateId,
		setLoading,
	});

	const {
		portOptions,
		chartererOptions,
		charterers,
	} = useMiscHandlers();

	return (
		<CargoContext.Provider
			value={{
				estimate,
				estimateLoading: false,
				cargos,
				cargoPorts: cargoPorts ?? [],
				onUpdateCargo,
				onUpdateCargoPort,
				copyCargoPortSettings,
				setSyncCargos,
				onCreateNewCargoMutator,
				onCreateNewCargoReletMutator,
				onImportCargoMutator,
				onDeleteCargoMutator,
				onDeleteCargoReletMutator,
				chartererOptions,
				charterers,
				portOptions,
				refreshEverything,
				hasPendingChanges,
				pendingCargoChanges,
				pendingCargoPortChanges,
				setPendingCargoPortChanges,
				setPendingCargoChanges,
				cargoReletConsumptionSet,
				hasCargoRelet,
				reletViewByEstimateId,
				setReletViewByEstimateId,
				handleSetReletView,
				showReletView,
			}}
		>
			{children}
		</CargoContext.Provider>
	);
};

export default CargoProvider;
